export type FileKind =
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'presentation'
  | 'archive'
  | 'code'
  | 'text'
  | 'other';

const EXTENSION_KINDS: Record<string, FileKind> = {
  pdf: 'pdf',
  doc: 'document',
  docx: 'document',
  odt: 'document',
  rtf: 'document',
  xls: 'spreadsheet',
  xlsx: 'spreadsheet',
  ods: 'spreadsheet',
  csv: 'spreadsheet',
  ppt: 'presentation',
  pptx: 'presentation',
  odp: 'presentation',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  json: 'code',
  xml: 'code',
  js: 'code',
  ts: 'code',
  cs: 'code',
  sql: 'code',
  html: 'code',
  css: 'code',
  txt: 'text',
  md: 'text',
  log: 'text',
};

export function getFileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : '';
}

/** Classifies a file by content type first, then by extension. */
export function getFileKind(name: string, contentType?: string | null): FileKind {
  const type = contentType?.toLowerCase() ?? '';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type === 'application/pdf') return 'pdf';
  return EXTENSION_KINDS[getFileExtension(name)] ?? (type.startsWith('text/') ? 'text' : 'other');
}

/** Whether the browser can preview the file inline (image, PDF, video, audio, text). */
export function isPreviewable(name: string, contentType?: string | null): boolean {
  const kind = getFileKind(name, contentType);
  return kind === 'image' || kind === 'pdf' || kind === 'video' || kind === 'audio' || kind === 'text';
}

const UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'] as const;

/** Human-readable size using Intl unit formatting ("1,2 МБ", "340 KB"). */
export function formatFileSize(bytes: number, locale: string): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit++;
  }
  if (unit === 0) return formatBytes(value, locale);
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: UNITS[unit],
    unitDisplay: 'short',
    maximumFractionDigits: unit === 0 || value >= 100 ? 0 : 1,
  }).format(value);
}

/**
 * Bytes as "425 B" / "425 Б": Intl's short English unit is the word ("425 byte"), so the symbol comes from the narrow
 * form and the spacing from the short kilobyte ("425 kB"), as the larger units show it.
 */
function formatBytes(value: number, locale: string): string {
  const symbol =
    new Intl.NumberFormat(locale, { style: 'unit', unit: 'byte', unitDisplay: 'narrow' })
      .formatToParts(value)
      .find((part) => part.type === 'unit')?.value ?? 'B';
  const kilobyte = new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: 'kilobyte',
    unitDisplay: 'short',
  }).formatToParts(1);
  const unitFirst =
    kilobyte.findIndex((part) => part.type === 'unit') <
    kilobyte.findIndex((part) => part.type === 'integer');
  const space = kilobyte.find((part) => part.type === 'literal')?.value ?? ' ';
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
  return unitFirst ? `${symbol}${space}${number}` : `${number}${space}${symbol}`;
}

/**
 * Whether `file` satisfies an `accept` list as used by `<input type="file">` (`image/*,.pdf,application/zip`): MIME
 * wildcards, exact MIME types and extensions. Dropped and pasted files bypass the input, so uploaders check this.
 */
export function matchesAccept(file: Pick<File, 'name' | 'type'>, accept: string | undefined): boolean {
  const tokens = (accept ?? '')
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
}
